import type {
  BrandColor,
  LegacyColor,
  TextColor,
  UIColor,
} from '@tokens/colortype';

/**
 * Rebranded base color palette.
 * Add additional gradients as required.
 */
export const BRAND_COLORS: Record<BrandColor, string> = {
  //Primary
  ember_100: '#FFF6F3',
  ember_200: '#FFECE6',
  ember_300: '#FFD9CD',
  ember_400: '#FFB49D',
  ember_500: '#FF855F',
  ember_600: '#FF3C00',
  ember_700: '#D23201',
  ember_800: '#A92902',
  ember_900: '#6F1C03',

  cobalt_100: '#F6F8FC',
  cobalt_200: '#EBEEF9',
  cobalt_300: '#DCE2F6',
  cobalt_400: '#BCC6EB',
  cobalt_500: '#96A5DF',
  cobalt_600: '#7084D2',
  cobalt_700: '#5069C8',
  cobalt_800: '#324FBE',
  cobalt_900: '#1C3286',

  midnight_600: '#202641',
  //System
  mint_100: '#F0FAF8',
  mint_200: '#E9F5F2',
  mint_300: '#D4E5E1',
  mint_400: '#B0CCC5',
  mint_500: '#8AAFA6',
  mint_600: '#5F9084',
  mint_700: '#3F796B',
  mint_800: '#226453',
  mint_900: '#0F4235',

  magenta_100: '#FEF6F6',
  magenta_200: '#FBEFEF',
  magenta_300: '#F3DBDB',
  magenta_400: '#E6BDBD',
  magenta_500: '#D69898',
  magenta_600: '#C57070',
  magenta_700: '#B54B4B',
  magenta_800: '#A72A2A',
  magenta_900: '#6E1B1B',
  //Secondary
  aqua_100: '#F9FDFE',
  aqua_200: '#F3FCFD',
  aqua_300: '#C3F0F7',
  aqua_400: '#90D0DA',
  blossom_100: '#FFF6F8',
  blossom_200: '#FFEEF3',
  blossom_300: '#FFCCDD',
  blossom_400: '#EFB8CA',
  lilac_100: '#F7F6FC',
  lilac_200: '#F3F1FA',
  lilac_300: '#E4DFF3',
  lilac_400: '#CCC2EB',
  lilac_600: '#9C89D4',
  dusk_100: '#FFFDFC',
  dusk_200: '#FFFBFA',
  dusk_300: '#FFF7F5',
  dusk_400: '#FFF4F0',
  dusk_500: '#FFF0EB',
  dusk_600: '#FFECE6',
  dusk_700: '#E6D4CF',
  dusk_800: '#CCBDB8',

  noon_100: '#FCFAE6',
  noon_200: '#F9F5CA',
  noon_300: '#F1E77F',
  noon_400: '#D6C93D',

  peach_400: '#E9C07C',
  peach_300: '#FADDAE',
  peach_200: '#FDF1DE',
  peach_100: '#FEF7EC',
  //Neutrals
  neutral_100: '#F8F9FC',
  neutral_200: '#F1F2F6',
  neutral_300: '#E0E2E8',
  neutral_400: '#C2C5CD',
  neutral_500: '#AFB2BD',
  neutral_600: '#838696',
  neutral_700: '#696D7F',
  neutral_800: '#54586B',
  neutral_900: '#34394D',
  neutral_950: '#191B24',

  white: '#FFFFFF',
};

/**
 * UI text colors
 */
const TEXT_COLORS: Record<TextColor, string> = {
  text_default: 'rgba(0, 0, 0, 0.89)',
  text_default_inverse: 'rgba(255, 255, 255, 0.89)',
  text_light: 'rgba(0, 0, 0, 0.70)',
  text_light_inverse: 'rgba(255, 255, 255, 0.60)',
  text_disabled: 'rgba(0, 0, 0, 0.54)',
  text_disabled_inverse: 'rgba(255, 255, 255, 0.38)',
  text_success: BRAND_COLORS.mint_800,
  text_error: BRAND_COLORS.magenta_600, //Deprecated replaced by text_danger
  text_danger: BRAND_COLORS.magenta_800,
  text_warning: BRAND_COLORS.ember_600,
};

/**
 * UI component colors.
 */
const UI_COLORS: Record<UIColor, string> = {
  primary_cta: BRAND_COLORS.cobalt_800,
  primary_cta_alt: BRAND_COLORS.cobalt_900,
  secondary_cta: BRAND_COLORS.cobalt_800,
  secondary_cta_alt: BRAND_COLORS.cobalt_900,
  danger_cta: BRAND_COLORS.magenta_800,
  danger_cta_alt: BRAND_COLORS.magenta_900,
  link: BRAND_COLORS.cobalt_800,
  link_alt: BRAND_COLORS.cobalt_900,
  focus: BRAND_COLORS.cobalt_500,
  bg_active: BRAND_COLORS.cobalt_200,
  bg_info: BRAND_COLORS.neutral_100,
  bg_success: BRAND_COLORS.mint_100,
  bg_danger: BRAND_COLORS.blossom_100,
  bg_warning: BRAND_COLORS.peach_100,
  ui_active: BRAND_COLORS.cobalt_700,
  ui_border: BRAND_COLORS.neutral_300,
  ui_border_alt: BRAND_COLORS.neutral_600,
  ui_border_light: BRAND_COLORS.neutral_200,
  ui_border_danger: BRAND_COLORS.magenta_600,
  ui_border_active: BRAND_COLORS.cobalt_700,
  ui_border_success: BRAND_COLORS.mint_300,
  ui_border_warning: BRAND_COLORS.peach_300,
  ui_background: BRAND_COLORS.white,
  ui_disabled: BRAND_COLORS.neutral_200,
  ui_overlay: 'rgba(0, 0, 0, 0.72)',
  bg_hover: 'rgba(121, 125, 141, 0.08)', // neutral_500, 8% opacity
};

/**
 * Legacy Colors that had no mapping to new colors
 */
const LEGACY_COLORS: Record<LegacyColor, string> = {
  bermuda: '#8D96A5',
  lilac: '#AB63C7',
  beer: '#EFB60C',
  azure: '#00A2F2',
  crimson_light: '#ED5E52',
  tangerine_light: '#F5AC37',
  lemon: '#FFD902',
};

/**
 * Rebranding colors with default colors and any color overrides applied.
 */
export const nextColor = {
  ...LEGACY_COLORS,
  ...BRAND_COLORS,
  ...TEXT_COLORS,
  ...UI_COLORS,
};

/**
 * returns a color in hex or rgb.
 *
 * includes both colors and aliases, where aliases should be prioritized and enough.
 *
 * example: `color.text_default` returns `rgba(0, 0, 0, 0.83)`
 */
export const color = nextColor;

/**
 * Returns UI color aliases.
 */
export const ALIAS_COLORS: Record<UIColor, string> = UI_COLORS;

/**
 * Returns Text color aliases.
 */
export const ALIAS_TEXT_COLORS: Record<TextColor, string> = TEXT_COLORS;
